import { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Send,
  Paperclip,
  Mic,
  MoreVertical,
  ArrowLeft,
  ArrowRight,
  Sparkles,
  Copy,
  RefreshCw,
  ThumbsUp,
  ThumbsDown,
  Zap,
  Check,
  MessageSquare,
  Plus,
  Clock,
  X,
  Loader2,
  Bot,
  User,
  Trash2,
  Download,
  Settings,
  Info,
  Edit3,
  CornerUpLeft,
  Volume2,
  VolumeX,
  Users,
} from 'lucide-react';
import { getEmployeeById } from '../data/employees';
import { useAuthStore } from '../store/auth';
import { useThemeStore } from '../store/theme';
import { useNavigate } from 'react-router-dom';
import MarkdownRenderer from '../components/MarkdownRenderer';
import { ConnectorActionCard, ConnectorEditModal, ConnectorResultCard } from '../components/chat';
import type { ConnectorProvider, ConnectorAction } from '../types';

const API_URL = import.meta.env.VITE_API_URL || '/api';

// Get user's preferred hour format from localStorage (shared with DateTimePicker)
const getPreferredHourFormat = (): '12' | '24' => {
  const saved = localStorage.getItem('preferredHourFormat');
  if (saved === '12' || saved === '24') return saved;
  // Try to detect from system locale
  const testDate = new Date(2024, 0, 1, 13, 0);
  const formatted = testDate.toLocaleTimeString();
  if (formatted.toLowerCase().includes('pm') || formatted.toLowerCase().includes('am')) {
    return '12';
  }
  return '24';
};

// Format time based on user preference
const formatTime = (date: Date): string => {
  const format = getPreferredHourFormat();
  if (format === '12') {
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }
  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

interface GeneratedImage {
  url: string;
  style?: string;
  revised_prompt?: string;
}

interface RouteSuggestion {
  employeeId: string;
  employeeName: string;
  employeeRole: string;
}

interface CollaborationResult {
  employeeId: string;
  employeeName: string;
  employeeRole: string;
  content: string;
}

interface PresentationSlide {
  number: number;
  title: string;
  content: string[];
  notes?: string;
  animation?: string;
  transition?: string;
  layout?: string;
}

interface PresentationData {
  id: string;
  title: string;
  subtitle?: string;
  author?: string;
  theme: string;
  style: string;
  slides: PresentationSlide[];
  metadata?: {
    totalSlides: number;
    createdAt: string;
    topic: string;
  };
}

interface GeneratedPresentation {
  id: string;
  data: PresentationData;
  credits: number;
}

// Connector types for integration actions
interface PendingConnectorAction {
  id: number;
  provider: ConnectorProvider;
  providerName: string;
  action: ConnectorAction;
  preview: Record<string, unknown>;
  actionData: Record<string, unknown>;
  requiresApproval: boolean;
  status?: 'pending' | 'approved' | 'rejected' | 'executed' | 'failed';
  result?: { success: boolean; data?: unknown; error?: string };
}

interface ConnectorResult {
  provider: ConnectorProvider;
  providerName: string;
  action: ConnectorAction;
  success: boolean;
  data?: unknown;
  error?: string;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  tokens_used?: number;
  images?: GeneratedImage[];
  imageUrl?: string; // For uploaded images
  routeSuggestions?: RouteSuggestion[]; // For Agent's routing suggestions
  collaborations?: CollaborationResult[]; // For cross-employee collaboration results
  presentations?: GeneratedPresentation[]; // For Slides employee presentations
  pendingActions?: PendingConnectorAction[]; // For connector write actions awaiting approval
  connectorResults?: ConnectorResult[]; // For connector read action results
}

interface Conversation {
  id: number;
  employee_id: string;
  title: string;
  last_message_at: string;
  message_count: number;
}

interface APIResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

interface ConversationsResponse {
  items: Conversation[];
  total: number;
}

interface MessageFromAPI {
  id: number;
  role: string;
  content: string;
  created_at: string;
  tokens_used?: number;
}

export default function EmployeeChat() {
  const { employeeId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, token, updateUser, updateCredits } = useAuthStore();
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const employee = getEmployeeById(employeeId || '');

  const [messages, setMessages] = useState<Message[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<number | null>(null);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [showUseCases, setShowUseCases] = useState(true);
  const [showSidebar, setShowSidebar] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [showImagePreview, setShowImagePreview] = useState<string | null>(null);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const [speakingMessageId, setSpeakingMessageId] = useState<string | null>(null);
  const [isNewConversation, setIsNewConversation] = useState(false);
  const [collaboratingWith, setCollaboratingWith] = useState<string | null>(null);
  const [routedFrom, setRoutedFrom] = useState<string | null>(null);
  const [pendingRoutedMessage, setPendingRoutedMessage] = useState<string | null>(null);

  // Connector state
  const [editingAction, setEditingAction] = useState<PendingConnectorAction | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<number | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const routedMessageProcessedRef = useRef<boolean>(false);

  // Auto-scroll to bottom - throttled during streaming to prevent shakiness
  const lastScrollTimeRef = useRef<number>(0);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevStreamingLengthRef = useRef<number>(0);

  useEffect(() => {
    const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
      messagesEndRef.current?.scrollIntoView({ behavior });
    };

    // For new messages (not during streaming), scroll smoothly
    if (messages.length > 0 && !streamingContent) {
      scrollToBottom('smooth');
      prevStreamingLengthRef.current = 0;
      return;
    }

    // For streaming content, throttle scrolling to reduce jitter
    if (streamingContent) {
      const now = Date.now();
      const contentGrowth = streamingContent.length - prevStreamingLengthRef.current;

      // Only scroll if content grew significantly or enough time passed
      if (contentGrowth > 50 || now - lastScrollTimeRef.current > 500) {
        lastScrollTimeRef.current = now;
        prevStreamingLengthRef.current = streamingContent.length;
        // Use instant scroll during active streaming to prevent animation conflicts
        scrollToBottom('instant');
      }

      // Clear any pending timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Schedule a smooth scroll when streaming settles
      scrollTimeoutRef.current = setTimeout(() => {
        scrollToBottom('smooth');
      }, 200);
    }

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [messages, streamingContent]);

  // Close more menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
        setShowMoreMenu(false);
      }
    };
    if (showMoreMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMoreMenu]);

  // Auto-resize textarea
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 128) + 'px';
    }
  }, [input]);

  // Reset routing state when employee changes
  useEffect(() => {
    routedMessageProcessedRef.current = false;
    setPendingRoutedMessage(null);
    setRoutedFrom(null);
  }, [employeeId]);

  // Load conversations on mount
  useEffect(() => {
    if (token && employeeId) {
      loadConversations();
    }
  }, [token, employeeId]);

  // Handle conversation from URL param
  useEffect(() => {
    const convId = searchParams.get('conversation');
    if (convId && token) {
      setCurrentConversationId(parseInt(convId));
      // Don't load messages if we just created a new conversation
      // (the first message is already in local state and being sent)
      if (isNewConversation) {
        setIsNewConversation(false);
      } else {
        loadMessages(parseInt(convId));
      }
    }
  }, [searchParams, token]);

  // Handle routed messages from other employees (e.g., Agent routing to Code)
  // Step 1: Detect routed message params and store them
  useEffect(() => {
    const isRouted = searchParams.get('routed') === 'true';
    const routedMessage = searchParams.get('message');
    const fromEmployee = searchParams.get('from');

    // Only process if this is a routed message, we have all params,
    // we haven't processed it yet, and we're on a different employee than the source
    if (isRouted && routedMessage && fromEmployee &&
        !routedMessageProcessedRef.current && token &&
        employeeId && fromEmployee !== employeeId) {

      routedMessageProcessedRef.current = true;

      // Store where the message came from for context display
      const sourceEmployee = getEmployeeById(fromEmployee);
      if (sourceEmployee) {
        setRoutedFrom(sourceEmployee.name);
      }

      // Store the message to send - decode it since it was URL encoded
      setPendingRoutedMessage(decodeURIComponent(routedMessage));

      // Clear the routed params from URL to prevent re-processing on refresh
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('routed');
      newParams.delete('message');
      newParams.delete('from');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, token, setSearchParams, employeeId]);

  const loadConversations = async () => {
    try {
      const response = await fetch(`${API_URL}/conversations?employee_id=${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = (await response.json()) as APIResponse<ConversationsResponse>;
      if (data.success && data.data) {
        setConversations(data.data.items || []);
      }
    } catch (error) {
      console.error('Failed to load conversations:', error);
    }
  };

  const loadMessages = async (conversationId: number) => {
    setIsLoadingHistory(true);
    try {
      const response = await fetch(`${API_URL}/conversations/${conversationId}/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = (await response.json()) as APIResponse<MessageFromAPI[]>;
      if (data.success && data.data) {
        setMessages(
          data.data.map((m) => ({
            id: m.id.toString(),
            role: m.role as 'user' | 'assistant',
            content: m.content,
            timestamp: new Date(m.created_at),
            tokens_used: m.tokens_used,
          }))
        );
        setShowUseCases(data.data.length === 0);
      } else {
        // Conversation not found or error - clear the invalid conversation
        console.warn('Conversation not found, resetting to new chat');
        setCurrentConversationId(null);
        setSearchParams({});
        setMessages([]);
        setShowUseCases(true);
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
      // Also handle network errors by resetting
      setCurrentConversationId(null);
      setSearchParams({});
      setMessages([]);
      setShowUseCases(true);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const createConversation = async (): Promise<number | null> => {
    try {
      const response = await fetch(`${API_URL}/conversations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          employee_id: employeeId,
          title: `Chat with ${employee?.name}`,
        }),
      });
      const data = (await response.json()) as APIResponse<Conversation>;
      if (data.success && data.data) {
        const newConv = data.data;
        setConversations((prev) => [newConv, ...prev]);
        setCurrentConversationId(newConv.id);
        // Mark as new conversation BEFORE setting searchParams
        // This prevents loadMessages from being called and overwriting local state
        setIsNewConversation(true);
        setSearchParams({ conversation: newConv.id.toString() });
        return newConv.id;
      }
    } catch (error) {
      console.error('Failed to create conversation:', error);
    }
    return null;
  };

  // Helper function to process streaming response
  const processStreamResponse = async (
    reader: ReadableStreamDefaultReader<Uint8Array>,
    messageContent: string,
    userMessage: Message
  ) => {
    const decoder = new TextDecoder();
    let fullContent = '';
    const generatedImages: Array<{ url: string; style?: string }> = [];
    const generatedPresentations: Array<{ id: string; data: PresentationData; credits: number }> = [];
    let routeSuggestions: RouteSuggestion[] = [];
    const collaborationResults: CollaborationResult[] = [];
    const pendingActions: PendingConnectorAction[] = [];
    const connectorResults: ConnectorResult[] = [];

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const text = decoder.decode(value, { stream: true });
      const lines = text.split('\n');

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          if (data === '[DONE]') {
            // Streaming complete - clean up any remaining command blocks and markdown images
            let cleanedContent = fullContent.replace(/\[EDIT_IMAGE\][\s\S]*?\[\/EDIT_IMAGE\]/g, '').trim();
            cleanedContent = cleanedContent.replace(/!\[.*?\]\(https?:\/\/[^\)]+\)/g, '').trim();
            cleanedContent = cleanedContent.replace(/\[CREATE_PRESENTATION\][\s\S]*?\[\/CREATE_PRESENTATION\]/g, '').trim();
            cleanedContent = cleanedContent.replace(/\[ROUTE_TO:\w+\]/g, '').trim();

            setMessages((prev) => [
              ...prev,
              {
                id: (Date.now() + 1).toString(),
                role: 'assistant',
                content: cleanedContent,
                timestamp: new Date(),
                images: generatedImages.length > 0 ? generatedImages : undefined,
                routeSuggestions: routeSuggestions.length > 0 ? routeSuggestions : undefined,
                collaborations: collaborationResults.length > 0 ? collaborationResults : undefined,
                presentations: generatedPresentations.length > 0 ? generatedPresentations : undefined,
                pendingActions: pendingActions.length > 0 ? pendingActions : undefined,
                connectorResults: connectorResults.length > 0 ? connectorResults : undefined,
              },
            ]);
            setStreamingContent('');
            setCollaboratingWith(null);

            // Check if Pixel's response contains [GENERATE_IMAGE] command
            if (employeeId === 'pixel' && fullContent.includes('[GENERATE_IMAGE]')) {
              const promptMatch = fullContent.match(/prompt:\s*(.+?)(?=\n|style:|$)/i);
              if (promptMatch) {
                const imagePrompt = promptMatch[1].trim();
                const logoMatch = messageContent.toLowerCase().match(/logo\s+(?:for\s+)?["']?([^"'\n,]+)["']?/i) ||
                                 messageContent.toLowerCase().match(/(?:create|make|design)\s+(?:a\s+)?logo\s+(?:for\s+)?["']?([^"'\n,]+)["']?/i);

                if (logoMatch) {
                  handleGenerateLogo(logoMatch[1].trim(), { description: imagePrompt });
                } else {
                  handleGenerateImage(imagePrompt, { n: 2 });
                }
              }
            }
          } else {
            try {
              const parsed = JSON.parse(data);
              if (parsed.content) {
                fullContent += parsed.content;
                // Strip EDIT_IMAGE blocks, ROUTE_TO commands, INVOKE_EMPLOYEE blocks, and CREATE_PRESENTATION blocks from display
                let displayContent = fullContent.replace(/\[EDIT_IMAGE\][\s\S]*?(?:\[\/EDIT_IMAGE\]|$)/g, '');
                displayContent = displayContent.replace(/\[ROUTE_TO:\w+\]/g, '');
                displayContent = displayContent.replace(/\[INVOKE_EMPLOYEE:\w+\][\s\S]*?(?:\[\/INVOKE_EMPLOYEE\]|$)/g, '');
                displayContent = displayContent.replace(/\[CREATE_PRESENTATION\][\s\S]*?(?:\[\/CREATE_PRESENTATION\]|$)/g, '');
                setStreamingContent(displayContent.trim());
              }
              if (parsed.generated_image) {
                generatedImages.push({
                  url: parsed.generated_image,
                  style: parsed.image_type || undefined,
                });
                // Strip EDIT_IMAGE blocks, ROUTE_TO commands, INVOKE_EMPLOYEE blocks, and CREATE_PRESENTATION blocks from display
                let displayContent = fullContent.replace(/\[EDIT_IMAGE\][\s\S]*?(?:\[\/EDIT_IMAGE\]|$)/g, '');
                displayContent = displayContent.replace(/\[ROUTE_TO:\w+\]/g, '');
                displayContent = displayContent.replace(/\[INVOKE_EMPLOYEE:\w+\][\s\S]*?(?:\[\/INVOKE_EMPLOYEE\]|$)/g, '');
                displayContent = displayContent.replace(/\[CREATE_PRESENTATION\][\s\S]*?(?:\[\/CREATE_PRESENTATION\]|$)/g, '');
                setStreamingContent(displayContent.trim() + `\n\n*Generating design ${generatedImages.length}...*`);
              }
              // Handle presentation generation
              if (parsed.presentation_generating) {
                setStreamingContent(prev => prev + `\n\n*📊 Creating ${parsed.presentation_slides}-slide presentation about "${parsed.presentation_topic}"...*`);
              }
              if (parsed.generated_presentation) {
                // Store presentation data for display
                generatedPresentations.push({
                  id: parsed.presentation_id,
                  data: parsed.generated_presentation,
                  credits: parsed.credits_used
                });
                setStreamingContent(prev => prev.replace(/\*📊 Creating.*?\.\.\.\*/, '') + `\n\n✅ Presentation created successfully!`);

                // Auto-redirect to presentation builder
                if (parsed.redirect_to_builder && parsed.presentation_id) {
                  setTimeout(() => {
                    navigate(`/dashboard/presentations/${parsed.presentation_id}`);
                  }, 1500); // Short delay to show success message
                }
              }
              if (parsed.presentation_error) {
                setStreamingContent(prev => prev + `\n\n❌ ${parsed.presentation_error}`);
              }
              if (parsed.route_suggestions && Array.isArray(parsed.route_suggestions)) {
                routeSuggestions = parsed.route_suggestions;
              }
              // Handle cross-employee collaboration
              if (parsed.collaboration_start) {
                setCollaboratingWith(parsed.collaboration_start.toEmployeeName);
                setStreamingContent(prev => prev + `\n\n*🤝 Collaborating with ${parsed.collaboration_start.toEmployeeName} (${parsed.collaboration_start.toEmployeeRole})...*`);
              }
              if (parsed.collaboration_result) {
                collaborationResults.push({
                  employeeId: parsed.collaboration_result.employeeId,
                  employeeName: parsed.collaboration_result.employeeName,
                  employeeRole: parsed.collaboration_result.employeeRole,
                  content: parsed.collaboration_result.content,
                });
                setCollaboratingWith(null);
              }
              if (parsed.collaboration_error) {
                setCollaboratingWith(null);
              }
              // Handle connector read results (immediate execution)
              if (parsed.connector_result) {
                connectorResults.push({
                  provider: parsed.connector_result.provider,
                  providerName: parsed.connector_result.providerName,
                  action: parsed.connector_result.action,
                  success: parsed.connector_result.success,
                  data: parsed.connector_result.data,
                  error: parsed.connector_result.error,
                });
                // Show status in streaming content
                const resultIcon = parsed.connector_result.success ? '✅' : '❌';
                setStreamingContent(prev => prev + `\n\n*${resultIcon} ${parsed.connector_result.providerName}: ${parsed.connector_result.action} completed*`);
              }
              // Handle connector write actions (need approval)
              if (parsed.pending_action) {
                pendingActions.push({
                  id: parsed.pending_action.id,
                  provider: parsed.pending_action.provider,
                  providerName: parsed.pending_action.providerName,
                  action: parsed.pending_action.action,
                  preview: parsed.pending_action.preview,
                  actionData: parsed.pending_action.actionData,
                  requiresApproval: parsed.pending_action.requiresApproval,
                  status: 'pending',
                });
                setStreamingContent(prev => prev + `\n\n*📝 Action pending approval: ${parsed.pending_action.providerName} - ${parsed.pending_action.action}*`);
              }
              // Handle connector errors
              if (parsed.connector_error) {
                console.error('Connector error:', parsed.connector_error);
                setStreamingContent(prev => prev + `\n\n*❌ ${parsed.connector_error.provider}: ${parsed.connector_error.error}*`);
              }
              if (parsed.done && typeof parsed.credits_remaining === 'number') {
                updateCredits(parsed.credits_remaining);
              }
              if (parsed.error) {
                throw new Error(parsed.error);
              }
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }
    }

    // Reload conversations to update last_message_at
    loadConversations();
  };

  const handleSend = async (routedContent?: string) => {
    const messageText = routedContent || input.trim();
    if (!messageText || isLoading || !token) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: messageText,
      timestamp: new Date(),
      imageUrl: routedContent ? undefined : (uploadedImage || undefined),
    };

    setMessages((prev) => [...prev, userMessage]);
    const messageContent = messageText;
    if (!routedContent) {
      setInput('');
    }
    setUploadedImage(null); // Clear uploaded image
    setIsLoading(true);
    setShowUseCases(false);
    setStreamingContent('');

    try {
      // Create conversation if needed or if current one doesn't exist
      let convId = currentConversationId;
      if (!convId) {
        convId = await createConversation();
        if (!convId) {
          throw new Error('Failed to create conversation');
        }
      }

      // Create abort controller for this request
      abortControllerRef.current = new AbortController();

      // Send message with streaming (include image if uploaded)
      const response = await fetch(`${API_URL}/conversations/${convId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          content: messageContent,
          image: userMessage.imageUrl || undefined, // Send base64 image if available
        }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        const errorData = (await response.json()) as APIResponse<unknown>;
        // If conversation not found, create a new one and retry
        if (response.status === 404 && errorData.error === 'Conversation not found') {
          console.warn('Conversation not found, creating new conversation and retrying...');
          setCurrentConversationId(null);
          setSearchParams({});
          convId = await createConversation();
          if (!convId) {
            throw new Error('Failed to create conversation');
          }
          // Retry the message send with new conversation
          const retryResponse = await fetch(`${API_URL}/conversations/${convId}/messages`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              content: messageContent,
              image: userMessage.imageUrl || undefined,
            }),
            signal: abortControllerRef.current.signal,
          });
          if (!retryResponse.ok) {
            const retryError = (await retryResponse.json()) as APIResponse<unknown>;
            throw new Error(retryError.error || 'Failed to send message');
          }
          // Continue with the retry response
          const reader = retryResponse.body?.getReader();
          if (!reader) throw new Error('No response body');
          await processStreamResponse(reader, messageContent, userMessage);
          return;
        }
        throw new Error(errorData.error || 'Failed to send message');
      }

      // Handle SSE streaming response
      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      await processStreamResponse(reader, messageContent, userMessage);
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        console.log('Request aborted');
      } else {
        console.error('Send message error:', error);
        // Determine user-friendly error message
        let errorMessage = 'Something went wrong. Please try again.';
        const errorStr = (error as Error).message || '';

        if (errorStr.includes('Failed to fetch') || errorStr.includes('NetworkError') || errorStr.includes('ERR_CONNECTION')) {
          errorMessage = 'Network connection lost. Please check your internet connection and try again.';
        } else if (errorStr.includes('timeout')) {
          errorMessage = 'Request timed out. Please try again.';
        } else if (errorStr) {
          errorMessage = `Sorry, I encountered an error: ${errorStr}. Please try again.`;
        }

        // Show error message
        setMessages((prev) => [
          ...prev,
          {
            id: (Date.now() + 1).toString(),
            role: 'assistant',
            content: errorMessage,
            timestamp: new Date(),
          },
        ]);
      }
    } finally {
      setIsLoading(false);
      setStreamingContent('');
      abortControllerRef.current = null;
    }
  };

  // Step 2: Process pending routed message after handleSend is available
  // We also need to ensure employee data is loaded before sending
  useEffect(() => {
    if (pendingRoutedMessage && !isLoading && token && employee && employeeId) {
      const messageToSend = pendingRoutedMessage;
      setPendingRoutedMessage(null); // Clear immediately to prevent double-send

      // Ensure we have a clean state for the new employee
      setCurrentConversationId(null);
      setMessages([]);
      setShowUseCases(false);

      // Delay to ensure React has fully updated all state
      const timer = setTimeout(() => {
        handleSend(messageToSend);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [pendingRoutedMessage, isLoading, token, employee, employeeId]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleUseCaseClick = (prompt: string) => {
    setInput(prompt);
    inputRef.current?.focus();
  };

  const handleNewChat = () => {
    setCurrentConversationId(null);
    setMessages([]);
    setShowUseCases(true);
    setSearchParams({});
  };

  const handleSelectConversation = (conv: Conversation) => {
    setCurrentConversationId(conv.id);
    setSearchParams({ conversation: conv.id.toString() });
    loadMessages(conv.id);
    setShowSidebar(false);
  };

  const handleCopy = useCallback(async (content: string, id: string) => {
    await navigator.clipboard.writeText(content);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  }, []);

  const handleRegenerate = useCallback(async () => {
    if (messages.length < 2) return;

    // Get the last user message
    const lastUserMessage = [...messages].reverse().find((m) => m.role === 'user');
    if (!lastUserMessage) return;

    // Remove the last assistant message
    setMessages((prev) => {
      const newMessages = [...prev];
      if (newMessages[newMessages.length - 1]?.role === 'assistant') {
        newMessages.pop();
      }
      return newMessages;
    });

    // Re-send the last user message
    setInput(lastUserMessage.content);
    setTimeout(() => handleSend(), 100);
  }, [messages]);

  // Start editing a user message
  const handleStartEdit = useCallback((message: Message) => {
    setEditingMessageId(message.id);
    setEditingContent(message.content);
  }, []);

  // Cancel editing
  const handleCancelEdit = useCallback(() => {
    setEditingMessageId(null);
    setEditingContent('');
  }, []);

  // Save edited message and re-send
  const handleSaveEdit = useCallback(async (messageId: string) => {
    if (!editingContent.trim() || isLoading || !token) return;

    // Find the message index
    const messageIndex = messages.findIndex(m => m.id === messageId);
    if (messageIndex === -1) return;

    const messageContent = editingContent.trim();

    // Remove all messages after this one (including the edited message)
    const newMessages = messages.slice(0, messageIndex);

    // Add the edited message
    const editedMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: messageContent,
      timestamp: new Date(),
    };

    setMessages([...newMessages, editedMessage]);
    setEditingMessageId(null);
    setEditingContent('');
    setShowUseCases(false);
    setIsLoading(true);
    setStreamingContent('');

    try {
      // Create conversation if needed
      let convId = currentConversationId;
      if (!convId) {
        convId = await createConversation();
        if (!convId) {
          throw new Error('Failed to create conversation');
        }
      }

      // Create abort controller for this request
      abortControllerRef.current = new AbortController();

      // Send message with streaming
      const response = await fetch(`${API_URL}/conversations/${convId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          content: messageContent,
        }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        const errorData = (await response.json()) as APIResponse<unknown>;
        throw new Error(errorData.error || 'Failed to send message');
      }

      // Handle SSE streaming response
      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let fullContent = '';
      const generatedImages: Array<{ url: string; style?: string }> = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const text = decoder.decode(value, { stream: true });
        const lines = text.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') {
              // Streaming complete
              let cleanedContent = fullContent.replace(/\[EDIT_IMAGE\][\s\S]*?\[\/EDIT_IMAGE\]/g, '').trim();
              cleanedContent = cleanedContent.replace(/!\[.*?\]\(https?:\/\/[^\)]+\)/g, '').trim();

              setMessages((prev) => [
                ...prev,
                {
                  id: (Date.now() + 1).toString(),
                  role: 'assistant',
                  content: cleanedContent,
                  timestamp: new Date(),
                  images: generatedImages.length > 0 ? generatedImages : undefined,
                },
              ]);
              setStreamingContent('');
            } else {
              try {
                const parsed = JSON.parse(data);
                if (parsed.content) {
                  fullContent += parsed.content;
                  const displayContent = fullContent.replace(/\[EDIT_IMAGE\][\s\S]*?(?:\[\/EDIT_IMAGE\]|$)/g, '').trim();
                  setStreamingContent(displayContent);
                }
                if (parsed.generated_image) {
                  generatedImages.push({
                    url: parsed.generated_image,
                    style: parsed.image_type || undefined,
                  });
                  const displayContent = fullContent.replace(/\[EDIT_IMAGE\][\s\S]*?(?:\[\/EDIT_IMAGE\]|$)/g, '').trim();
                  setStreamingContent(displayContent + `\n\n*Generating design ${generatedImages.length}...*`);
                }
                if (parsed.done && typeof parsed.credits_remaining === 'number') {
                  updateCredits(parsed.credits_remaining);
                }
                if (parsed.error) {
                  throw new Error(parsed.error);
                }
              } catch (e) {
                // Skip invalid JSON
              }
            }
          }
        }
      }

      // Reload conversations to update last_message_at
      loadConversations();
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        console.log('Request aborted');
      } else {
        console.error('Send message error:', error);
        let errorMessage = 'Something went wrong. Please try again.';
        const errorStr = (error as Error).message || '';

        if (errorStr.includes('Failed to fetch') || errorStr.includes('NetworkError') || errorStr.includes('ERR_CONNECTION')) {
          errorMessage = 'Network connection lost. Please check your internet connection and try again.';
        } else if (errorStr.includes('timeout')) {
          errorMessage = 'Request timed out. Please try again.';
        } else if (errorStr) {
          errorMessage = `Sorry, I encountered an error: ${errorStr}. Please try again.`;
        }

        setMessages((prev) => [
          ...prev,
          {
            id: (Date.now() + 1).toString(),
            role: 'assistant',
            content: errorMessage,
            timestamp: new Date(),
          },
        ]);
      }
    } finally {
      setIsLoading(false);
      setStreamingContent('');
      abortControllerRef.current = null;
    }
  }, [editingContent, isLoading, messages, token, currentConversationId, updateCredits]);

  // Re-prompt: Use a user message to send again
  const handleReprompt = useCallback((message: Message) => {
    setInput(message.content);
    inputRef.current?.focus();
  }, []);

  // Connector action handlers
  const handleApproveAction = useCallback(async (actionId: number) => {
    if (!token) return;
    setActionLoadingId(actionId);

    try {
      const response = await fetch(`${API_URL}/connectors/pending/${actionId}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json() as { success: boolean; data?: unknown; error?: string };

      // Update the action status in the message
      setMessages(prev => prev.map(msg => {
        if (!msg.pendingActions) return msg;
        return {
          ...msg,
          pendingActions: msg.pendingActions.map(action =>
            action.id === actionId
              ? { ...action, status: result.success ? 'executed' : 'failed', result }
              : action
          ),
        } as Message;
      }));

      if (result.success) {
        // Could show a success toast here
      }
    } catch (error) {
      console.error('Failed to approve action:', error);
      // Update status to failed
      setMessages(prev => prev.map(msg => {
        if (!msg.pendingActions) return msg;
        return {
          ...msg,
          pendingActions: msg.pendingActions.map(action =>
            action.id === actionId
              ? { ...action, status: 'failed', result: { success: false, error: 'Failed to execute action' } }
              : action
          ),
        } as Message;
      }));
    } finally {
      setActionLoadingId(null);
    }
  }, [token]);

  const handleEditAction = useCallback((actionId: number, action: PendingConnectorAction) => {
    setEditingAction(action);
    setIsEditModalOpen(true);
  }, []);

  const handleSaveEditedAction = useCallback(async (actionId: number, editedData: Record<string, unknown>) => {
    if (!token) return;
    setActionLoadingId(actionId);

    try {
      const response = await fetch(`${API_URL}/connectors/pending/${actionId}/edit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ editedData }),
      });

      const result = await response.json() as { success: boolean; data?: unknown; error?: string };

      // Update the action status in the message
      setMessages(prev => prev.map(msg => {
        if (!msg.pendingActions) return msg;
        return {
          ...msg,
          pendingActions: msg.pendingActions.map(action =>
            action.id === actionId
              ? { ...action, status: result.success ? 'executed' : 'failed', result, actionData: editedData }
              : action
          ),
        } as Message;
      }));

      setIsEditModalOpen(false);
      setEditingAction(null);
    } catch (error) {
      console.error('Failed to save edited action:', error);
    } finally {
      setActionLoadingId(null);
    }
  }, [token]);

  const handleRejectAction = useCallback(async (actionId: number) => {
    if (!token) return;
    setActionLoadingId(actionId);

    try {
      await fetch(`${API_URL}/connectors/pending/${actionId}/reject`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Update the action status in the message
      setMessages(prev => prev.map(msg => {
        if (!msg.pendingActions) return msg;
        return {
          ...msg,
          pendingActions: msg.pendingActions.map(action =>
            action.id === actionId
              ? { ...action, status: 'rejected' }
              : action
          ),
        } as Message;
      }));
    } catch (error) {
      console.error('Failed to reject action:', error);
    } finally {
      setActionLoadingId(null);
    }
  }, [token]);

  // Copy user message
  const handleCopyUserMessage = useCallback(async (content: string, id: string) => {
    await navigator.clipboard.writeText(content);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  }, []);

  // Text-to-speech handler
  const handleSpeak = useCallback((content: string, id: string) => {
    // Check if already speaking this message
    if (speakingMessageId === id) {
      // Stop speaking
      window.speechSynthesis.cancel();
      setSpeakingMessageId(null);
      return;
    }

    // Stop any current speech
    window.speechSynthesis.cancel();

    // Strip markdown formatting for cleaner speech
    const cleanContent = content
      .replace(/^#{1,6}\s*/gm, '') // Remove heading markers (# ## ### etc.)
      .replace(/\*\*([^*]+)\*\*/g, '$1') // Remove bold **text**
      .replace(/\*([^*]+)\*/g, '$1') // Remove italic *text*
      .replace(/__([^_]+)__/g, '$1') // Remove bold __text__
      .replace(/_([^_]+)_/g, '$1') // Remove italic _text_
      .replace(/`([^`]+)`/g, '$1') // Remove inline code `text`
      .replace(/```[\s\S]*?```/g, '') // Remove code blocks
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Convert links to just text
      .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1 image') // Convert images to "alt text image"
      .replace(/^\s*[-*+]\s+/gm, '') // Remove list markers
      .replace(/^\s*\d+\.\s+/gm, '') // Remove numbered list markers
      .replace(/>\s*/gm, '') // Remove blockquote markers
      .replace(/\n{2,}/g, '. ') // Convert multiple newlines to pauses
      .replace(/\n/g, ' ') // Convert single newlines to spaces
      .trim();

    // Create utterance
    const utterance = new SpeechSynthesisUtterance(cleanContent);
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;

    // Try to use a good voice
    const voices = window.speechSynthesis.getVoices();
    const preferredVoice = voices.find(v =>
      v.lang.startsWith('en') && (v.name.includes('Google') || v.name.includes('Microsoft') || v.name.includes('Samantha'))
    ) || voices.find(v => v.lang.startsWith('en'));

    if (preferredVoice) {
      utterance.voice = preferredVoice;
    }

    // Set callbacks
    utterance.onend = () => {
      setSpeakingMessageId(null);
    };
    utterance.onerror = () => {
      setSpeakingMessageId(null);
    };

    // Start speaking
    setSpeakingMessageId(id);
    window.speechSynthesis.speak(utterance);
  }, [speakingMessageId]);

  // Stop speech when component unmounts or conversation changes
  useEffect(() => {
    return () => {
      window.speechSynthesis.cancel();
    };
  }, [currentConversationId]);

  // Handle virtual keyboard on mobile - scroll to keep input visible
  useEffect(() => {
    const handleVisualViewportResize = () => {
      if (window.visualViewport && inputRef.current) {
        // When keyboard opens, the visual viewport height decreases
        // Scroll the chat to keep input visible
        const visualViewportHeight = window.visualViewport.height;
        const windowHeight = window.innerHeight;
        const keyboardHeight = windowHeight - visualViewportHeight;

        if (keyboardHeight > 100) {
          // Keyboard is open - scroll to bottom to keep input in view
          setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
          }, 100);
        }
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleVisualViewportResize);
      return () => {
        window.visualViewport?.removeEventListener('resize', handleVisualViewportResize);
      };
    }
  }, []);

  // Lock screen orientation to portrait on mobile
  useEffect(() => {
    const lockOrientation = async () => {
      try {
        // Try to lock screen orientation using Screen Orientation API
        if (screen.orientation && 'lock' in screen.orientation) {
          await (screen.orientation as ScreenOrientation & { lock: (orientation: string) => Promise<void> }).lock('portrait');
        }
      } catch (e) {
        // Orientation lock not supported or failed - this is expected on many devices
        console.log('Screen orientation lock not available');
      }
    };

    lockOrientation();

    return () => {
      // Unlock orientation when leaving the chat
      try {
        if (screen.orientation && 'unlock' in screen.orientation) {
          (screen.orientation as ScreenOrientation & { unlock: () => void }).unlock();
        }
      } catch (e) {
        // Ignore errors
      }
    };
  }, []);

  // Handle file upload for Pixel
  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check if it's an image
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    // Convert to base64 for preview and sending
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result as string;
      setUploadedImage(base64);
    };
    reader.readAsDataURL(file);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // Generate logo/design for Pixel (3 credits per logo)
  const handleGenerateLogo = useCallback(async (brandName: string, options?: {
    industry?: string;
    style?: string;
    colors?: string;
    description?: string;
  }) => {
    if (!token || !brandName) return;

    setIsGeneratingImage(true);
    try {
      interface LogoResponse {
        success: boolean;
        data?: {
          logos: GeneratedImage[];
          count: number;
          credits_used?: number;
          credits_remaining?: number;
        };
        error?: string;
      }

      const response = await fetch(`${API_URL}/images/generate-logo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          brandName,
          industry: options?.industry,
          style: options?.style,
          colors: options?.colors,
          description: options?.description,
          variations: 4,
        }),
      });

      const data = (await response.json()) as LogoResponse;

      if (data.success && data.data) {
        // Update credits if returned
        if (typeof data.data.credits_remaining === 'number') {
          updateCredits(data.data.credits_remaining);
        }

        // Add message with generated images
        const imageMessage: Message = {
          id: Date.now().toString(),
          role: 'assistant',
          content: `I've created ${data.data.count} logo variations for "${brandName}". Click on any image to view it larger, or let me know if you'd like any modifications!`,
          timestamp: new Date(),
          images: data.data.logos,
        };
        setMessages((prev) => [...prev, imageMessage]);
      } else {
        throw new Error(data.error || 'Failed to generate logos');
      }
    } catch (error) {
      console.error('Logo generation error:', error);
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now().toString(),
          role: 'assistant',
          content: `Sorry, I couldn't generate the logos. ${error instanceof Error ? error.message : 'Please try again.'}`,
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsGeneratingImage(false);
    }
  }, [token, updateCredits]);

  // Generate image from prompt
  // Type determines credit cost: 'image' = 1 credit, 'logo' = 3 credits, 'poster' = 5 credits
  const handleGenerateImage = useCallback(async (prompt: string, options?: {
    style?: string;
    size?: string;
    n?: number;
    type?: 'image' | 'logo' | 'poster';
  }) => {
    if (!token || !prompt) return;

    setIsGeneratingImage(true);
    try {
      interface ImageResponse {
        success: boolean;
        data?: {
          images: GeneratedImage[];
          count: number;
          credits_used?: number;
          credits_remaining?: number;
        };
        error?: string;
      }

      const response = await fetch(`${API_URL}/images/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          prompt,
          style: options?.style || 'vivid',
          size: options?.size || '1024x1024',
          n: options?.n || 1,
          type: options?.type || 'image',
        }),
      });

      const data = (await response.json()) as ImageResponse;

      if (data.success && data.data) {
        // Update credits if returned
        if (typeof data.data.credits_remaining === 'number') {
          updateCredits(data.data.credits_remaining);
        }

        const imageMessage: Message = {
          id: Date.now().toString(),
          role: 'assistant',
          content: `Here's the image I created based on your request. Click to view larger or let me know if you'd like any changes!`,
          timestamp: new Date(),
          images: data.data.images,
        };
        setMessages((prev) => [...prev, imageMessage]);
      } else {
        throw new Error(data.error || 'Failed to generate image');
      }
    } catch (error) {
      console.error('Image generation error:', error);
      // Show error message to user
      const errorMessage: Message = {
        id: Date.now().toString(),
        role: 'assistant',
        content: `Sorry, I couldn't generate the image. ${error instanceof Error ? error.message : 'Please try again later.'}`,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsGeneratingImage(false);
    }
  }, [token, updateCredits]);

  // Download image - uses proxy to bypass CORS for DALL-E images
  const handleDownloadImage = useCallback(async (url: string, filename: string) => {
    try {
      // Check if URL is from DALL-E (Azure blob storage) - needs proxy
      const needsProxy = url.includes('blob.core.windows.net') || url.includes('openai.com');

      let downloadUrl = url;
      if (needsProxy) {
        // Use our proxy endpoint to bypass CORS
        downloadUrl = `${API_URL}/images/proxy?url=${encodeURIComponent(url)}`;
      }

      const response = await fetch(downloadUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        throw new Error('Failed to download image');
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download error:', error);
      // Fallback: open image in new tab
      window.open(url, '_blank');
    }
  }, [token]);

  // Clear current conversation
  const handleClearChat = useCallback(() => {
    if (confirm('Are you sure you want to clear this conversation? This cannot be undone.')) {
      setMessages([]);
      setShowUseCases(true);
      setShowMoreMenu(false);
    }
  }, []);

  // Export chat as text file
  const handleExportChat = useCallback(() => {
    if (messages.length === 0) {
      alert('No messages to export');
      return;
    }

    const exportContent = messages.map(m => {
      const sender = m.role === 'user' ? 'You' : employee?.name || 'Assistant';
      const dateStr = m.timestamp.toLocaleDateString();
      const timeStr = formatTime(m.timestamp);
      return `[${dateStr} ${timeStr}] ${sender}:\n${m.content}\n`;
    }).join('\n---\n\n');

    const blob = new Blob([exportContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `chat-${employee?.name || 'export'}-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowMoreMenu(false);
  }, [messages, employee]);

  // Check if message content contains logo/design request for Pixel
  const isDesignRequest = useCallback((content: string): boolean => {
    if (employeeId !== 'pixel') return false;
    const designKeywords = ['logo', 'design', 'create', 'make', 'generate', 'brand', 'icon', 'graphic'];
    const lowerContent = content.toLowerCase();
    return designKeywords.some(keyword => lowerContent.includes(keyword));
  }, [employeeId]);

  if (!employee) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className={`w-20 h-20 rounded-3xl flex items-center justify-center mb-6 ${isDark ? 'bg-dark-800' : 'bg-slate-200'}`}>
          <Bot className={`w-10 h-10 ${isDark ? 'text-gray-500' : 'text-slate-400'}`} />
        </div>
        <h2 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
          Employee not found
        </h2>
        <p className={`mb-6 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>This AI employee doesn't exist or has been removed.</p>
        <Link to="/dashboard/employees" className="px-6 py-3 bg-purple-500 hover:bg-purple-600 rounded-xl text-white font-medium transition-colors">
          Back to Employees
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100dvh-3.5rem)] sm:h-[calc(100vh-8rem)] -mx-3 sm:-mx-4 lg:-mx-8 -mt-3 sm:-mt-4 lg:-mt-8 fixed sm:relative inset-x-0 sm:inset-auto top-14 sm:top-auto bottom-0 sm:bottom-auto z-20 sm:z-auto">
      {/* Conversation Sidebar */}
      <AnimatePresence>
        {showSidebar && (
          <>
            {/* Mobile overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
              onClick={() => setShowSidebar(false)}
            />
            {/* Sidebar */}
            <motion.div
              initial={{ x: -300, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -300, opacity: 0 }}
              className={`fixed lg:relative left-0 top-0 h-full w-80 backdrop-blur-xl border-r z-50 lg:z-auto flex flex-col ${isDark ? 'bg-dark-800/95 border-white/5' : 'bg-white/95 border-slate-200'}`}
            >
              {/* Sidebar Header */}
              <div className={`p-4 border-b ${isDark ? 'border-white/5' : 'border-slate-200'}`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Chat History</h3>
                  <button
                    onClick={() => setShowSidebar(false)}
                    className={`p-2 rounded-lg transition-colors lg:hidden ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-100'}`}
                  >
                    <X className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-slate-500'}`} />
                  </button>
                </div>
                <motion.button
                  onClick={handleNewChat}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl font-medium text-white shadow-lg shadow-purple-500/20"
                >
                  <Plus className="w-4 h-4" />
                  <span>New Chat</span>
                </motion.button>
              </div>

              {/* Conversations List */}
              <div className="flex-1 overflow-y-auto p-3 space-y-1">
                {conversations.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageSquare className={`w-10 h-10 mx-auto mb-3 ${isDark ? 'text-gray-600' : 'text-slate-400'}`} />
                    <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>No conversations yet</p>
                    <p className={`text-xs mt-1 ${isDark ? 'text-gray-600' : 'text-slate-400'}`}>Start a new chat to begin</p>
                  </div>
                ) : (
                  conversations.map((conv) => (
                    <motion.button
                      key={conv.id}
                      onClick={() => handleSelectConversation(conv)}
                      whileHover={{ x: 4 }}
                      className={`w-full text-left px-4 py-3 rounded-xl transition-all ${
                        currentConversationId === conv.id
                          ? isDark ? 'bg-purple-500/20 border border-purple-500/30 text-white' : 'bg-purple-100 border border-purple-300 text-purple-900'
                          : isDark ? 'hover:bg-white/5 text-gray-400' : 'hover:bg-slate-100 text-slate-600'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <MessageSquare className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate text-sm font-medium">{conv.title}</span>
                      </div>
                      <div className={`flex items-center gap-2 mt-1.5 text-xs ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                        <Clock className="w-3 h-3" />
                        <span>{conv.message_count || 0} messages</span>
                      </div>
                    </motion.button>
                  ))
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Main Chat Area */}
      <div className={`flex-1 flex flex-col min-w-0 min-h-0 w-full ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
        {/* Header - Messenger style on mobile */}
        <div className={`flex items-center justify-between px-3 pt-3 pb-2.5 sm:p-3 sm:p-4 lg:px-8 border-b backdrop-blur-sm relative z-30 ${isDark ? 'border-white/5 bg-dark-800 sm:bg-dark-800/50' : 'border-slate-200 bg-white sm:bg-white/80'}`}>
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Link
              to="/dashboard/employees"
              className={`p-1.5 sm:p-2 rounded-full sm:rounded-xl transition-colors flex-shrink-0 ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-100'}`}
            >
              <ArrowLeft className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-slate-500'}`} />
            </Link>
            <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
              <div
                className={`w-9 h-9 sm:w-12 sm:h-12 rounded-full sm:rounded-2xl bg-gradient-to-br ${employee.gradient} flex items-center justify-center shadow-lg flex-shrink-0`}
              >
                <span className="text-base sm:text-xl">{employee.avatar}</span>
              </div>
              <div className="min-w-0">
                <h1 className={`text-sm sm:text-lg font-semibold truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>{employee.name}</h1>
                <div className="flex items-center gap-1 sm:gap-2">
                  <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-green-400 animate-pulse flex-shrink-0" />
                  <p className={`text-[10px] sm:text-sm truncate ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>{employee.role}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-0.5 sm:gap-2 flex-shrink-0">
            <button
              onClick={() => setShowSidebar(!showSidebar)}
              className={`p-1.5 sm:p-2 rounded-full sm:rounded-xl transition-colors lg:hidden ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-100'}`}
              title="Chat History"
            >
              <MessageSquare className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-slate-500'}`} />
            </button>
            <motion.button
              onClick={handleNewChat}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`p-1.5 sm:p-2.5 rounded-full sm:rounded-xl transition-colors ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-100'}`}
              title="New Chat"
            >
              <Plus className={`w-5 h-5 sm:w-5 sm:h-5 ${isDark ? 'text-gray-400' : 'text-slate-500'}`} />
            </motion.button>
            <button
              onClick={() => setShowSidebar(!showSidebar)}
              className={`p-2 sm:p-2.5 rounded-xl transition-colors hidden lg:block ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-100'}`}
              title="Chat History"
            >
              <MessageSquare className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-slate-500'}`} />
            </button>
            <div className="relative" ref={moreMenuRef}>
              <button
                onClick={() => setShowMoreMenu(!showMoreMenu)}
                className={`p-1.5 sm:p-2.5 rounded-full sm:rounded-xl transition-colors ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-100'}`}
              >
                <MoreVertical className={`w-5 h-5 sm:w-5 sm:h-5 ${isDark ? 'text-gray-400' : 'text-slate-500'}`} />
              </button>

              {/* More Menu Dropdown */}
              <AnimatePresence>
                {showMoreMenu && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -10 }}
                    transition={{ duration: 0.15 }}
                    className={`absolute right-0 top-full mt-2 w-48 border rounded-xl shadow-xl overflow-hidden z-50 ${isDark ? 'bg-dark-800 border-white/10' : 'bg-white border-slate-200'}`}
                  >
                    <div className="py-1">
                      <button
                        onClick={handleExportChat}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${isDark ? 'text-gray-300 hover:bg-white/5' : 'text-slate-700 hover:bg-slate-100'}`}
                      >
                        <Download className="w-4 h-4" />
                        Export Chat
                      </button>
                      <button
                        onClick={handleClearChat}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${isDark ? 'text-gray-300 hover:bg-white/5' : 'text-slate-700 hover:bg-slate-100'}`}
                      >
                        <Trash2 className="w-4 h-4" />
                        Clear Chat
                      </button>
                      <div className={`border-t my-1 ${isDark ? 'border-white/5' : 'border-slate-200'}`} />
                      <Link
                        to="/dashboard/employees"
                        onClick={() => setShowMoreMenu(false)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${isDark ? 'text-gray-300 hover:bg-white/5' : 'text-slate-700 hover:bg-slate-100'}`}
                      >
                        <Settings className="w-4 h-4" />
                        All Employees
                      </Link>
                      <div
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm cursor-default ${isDark ? 'text-gray-500' : 'text-slate-400'}`}
                      >
                        <Info className="w-4 h-4" />
                        {employee.role}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden px-3 sm:px-4 lg:px-8 py-3 sm:py-6 relative z-10">
          {/* Routed from another employee indicator */}
          {routedFrom && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-4xl mx-auto mb-4"
            >
              <div className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm ${
                isDark
                  ? 'bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border border-purple-500/20 text-purple-300'
                  : 'bg-gradient-to-r from-purple-50 to-cyan-50 border border-purple-200 text-purple-700'
              }`}>
                <Users className="w-4 h-4 flex-shrink-0" />
                <span>
                  Routed from <span className="font-semibold">{routedFrom}</span> — {employee?.name} will continue helping you with your request.
                </span>
                <button
                  onClick={() => setRoutedFrom(null)}
                  className={`ml-auto p-1 rounded-lg transition-colors ${
                    isDark ? 'hover:bg-white/10' : 'hover:bg-purple-100'
                  }`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </motion.div>
          )}

          {/* Loading History */}
          {isLoadingHistory && (
            <div className="flex justify-center py-8">
              <div className={`flex items-center gap-3 px-4 py-2 rounded-full ${isDark ? 'bg-dark-800/50' : 'bg-white shadow-sm'}`}>
                <Loader2 className="w-4 h-4 text-purple-400 animate-spin" />
                <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Loading conversation...</span>
              </div>
            </div>
          )}

          {/* Welcome Message */}
          {messages.length === 0 && !isLoadingHistory && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-2xl mx-auto text-center py-4 sm:py-12 px-1"
            >
              <motion.div
                className={`w-14 h-14 sm:w-24 sm:h-24 rounded-full sm:rounded-3xl bg-gradient-to-br ${employee.gradient} flex items-center justify-center mx-auto mb-3 sm:mb-6 shadow-2xl`}
                animate={{
                  y: [0, -8, 0],
                  rotate: [0, 2, -2, 0],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <span className="text-xl sm:text-4xl">{employee.avatar}</span>
              </motion.div>
              <h2 className={`text-lg sm:text-2xl lg:text-3xl font-bold mb-1 sm:mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Hi, I'm {employee.name}!
              </h2>
              <p className={`text-xs sm:text-base max-w-md mx-auto mb-4 sm:mb-10 leading-relaxed px-2 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                {employee.description}
              </p>

              {/* Quick Actions / Use Cases */}
              {showUseCases && (
                <div>
                  <p className={`text-[10px] sm:text-sm mb-2 sm:mb-4 ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                    Try one of these:
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-2 gap-1.5 sm:gap-3 max-w-xl mx-auto">
                    {employee.capabilities.slice(0, 4).map((capability, index) => (
                      <motion.button
                        key={capability}
                        onClick={() => handleUseCaseClick(`Help me with ${capability.toLowerCase()}`)}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        whileHover={{ y: -2 }}
                        className={`flex items-center gap-1.5 sm:gap-3 p-2 sm:p-4 rounded-lg sm:rounded-2xl border text-left group transition-all ${isDark ? 'bg-dark-800/50 border-white/5 hover:border-purple-500/30 hover:bg-dark-800' : 'bg-white border-slate-200 hover:border-purple-300 hover:bg-purple-50'}`}
                      >
                        <div className={`w-7 h-7 sm:w-10 sm:h-10 rounded-md sm:rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${isDark ? 'bg-purple-500/10 group-hover:bg-purple-500/20' : 'bg-purple-100 group-hover:bg-purple-200'}`}>
                          <Zap className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-purple-400" />
                        </div>
                        <span className={`text-[10px] sm:text-sm transition-colors line-clamp-2 leading-tight ${isDark ? 'text-gray-400 group-hover:text-white' : 'text-slate-600 group-hover:text-slate-900'}`}>
                          {capability}
                        </span>
                      </motion.button>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Messages */}
          <div className="max-w-4xl mx-auto space-y-2 sm:space-y-4 w-full overflow-hidden">
            <AnimatePresence>
              {messages.map((message, index) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className={`flex w-full ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`flex gap-1.5 sm:gap-3 max-w-[88%] sm:max-w-[80%] lg:max-w-[70%] min-w-0 ${
                      message.role === 'user' ? 'flex-row-reverse' : ''
                    }`}
                  >
                    {/* Avatar - Hidden on mobile for cleaner look */}
                    <div className="hidden sm:flex flex-shrink-0 mt-1">
                      {message.role === 'assistant' ? (
                        <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full sm:rounded-xl bg-gradient-to-br ${employee.gradient} flex items-center justify-center shadow-lg`}>
                          <span className="text-sm">{employee.avatar}</span>
                        </div>
                      ) : (
                        <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full sm:rounded-xl bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center shadow-lg">
                          <User className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                        </div>
                      )}
                    </div>

                    {/* Message Content - Plain on mobile, bubble on desktop */}
                    <div
                      className={`sm:rounded-2xl px-2 py-1 sm:px-4 sm:py-3 min-w-0 overflow-hidden ${
                        message.role === 'user'
                          ? `sm:bg-gradient-to-br sm:from-purple-500 sm:to-purple-600 sm:text-white sm:rounded-br-md ${isDark ? 'text-white' : 'text-slate-900'}`
                          : isDark
                            ? 'sm:bg-dark-800/80 sm:backdrop-blur-sm sm:border sm:border-white/5 sm:rounded-bl-md'
                            : 'sm:bg-white/90 sm:backdrop-blur-sm sm:border sm:border-slate-200 sm:rounded-bl-md sm:shadow-sm'
                      }`}
                    >
                      {message.role === 'assistant' && (
                        <div className={`hidden sm:flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2 pb-1.5 sm:pb-2 border-b ${isDark ? 'border-white/5' : 'border-slate-100'}`}>
                          <span className={`text-xs sm:text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{employee.name}</span>
                          <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>•</span>
                          <span className={`text-[10px] sm:text-xs ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                            {formatTime(message.timestamp)}
                          </span>
                        </div>
                      )}

                      {message.role === 'user' ? (
                        <>
                          {/* User message header with timestamp */}
                          <div className={`hidden sm:flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2 pb-1.5 sm:pb-2 border-b border-white/10`}>
                            <span className={`text-xs sm:text-sm font-medium text-white`}>You</span>
                            <span className={`text-xs text-white/50`}>•</span>
                            <span className={`text-[10px] sm:text-xs text-white/60`}>
                              {formatTime(message.timestamp)}
                            </span>
                          </div>
                          {editingMessageId === message.id ? (
                            // Editing UI
                            <div className="space-y-2">
                              <textarea
                                value={editingContent}
                                onChange={(e) => setEditingContent(e.target.value)}
                                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-sm text-white placeholder-white/50 focus:outline-none focus:border-purple-400 resize-none"
                                rows={3}
                                autoFocus
                              />
                              <div className="flex gap-2 justify-end">
                                <button
                                  onClick={handleCancelEdit}
                                  className="px-3 py-1.5 text-xs rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={() => handleSaveEdit(message.id)}
                                  disabled={!editingContent.trim() || isLoading}
                                  className="px-3 py-1.5 text-xs rounded-lg bg-purple-500 hover:bg-purple-600 text-white transition-colors disabled:opacity-50"
                                >
                                  Save & Send
                                </button>
                              </div>
                            </div>
                          ) : (
                            // Normal message display
                            <>
                              <div className={`text-[13px] sm:text-sm leading-relaxed whitespace-pre-wrap ${isDark ? 'text-white' : 'text-slate-900 sm:text-white'}`}>
                                {message.content}
                              </div>
                              {/* Show uploaded image in user message */}
                              {message.imageUrl && (
                                <div className="mt-3">
                                  <img
                                    src={message.imageUrl}
                                    alt="Uploaded"
                                    className="max-w-xs rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                                    onClick={() => setShowImagePreview(message.imageUrl || null)}
                                  />
                                </div>
                              )}
                              {/* User message actions */}
                              <div className={`flex items-center gap-1 mt-2 pt-2 border-t ${isDark ? 'border-white/10' : 'border-slate-200 sm:border-white/10'}`}>
                                <motion.button
                                  onClick={() => handleCopyUserMessage(message.content, message.id)}
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  className={`p-1.5 rounded-lg transition-colors group ${isDark ? 'hover:bg-white/10' : 'hover:bg-slate-200 sm:hover:bg-white/10'}`}
                                  title="Copy"
                                >
                                  {copiedId === message.id ? (
                                    <Check className="w-3.5 h-3.5 text-green-400" />
                                  ) : (
                                    <Copy className={`w-3.5 h-3.5 ${isDark ? 'text-white/70 group-hover:text-white' : 'text-slate-500 group-hover:text-slate-700 sm:text-white/70 sm:group-hover:text-white'}`} />
                                  )}
                                </motion.button>
                                <motion.button
                                  onClick={() => handleStartEdit(message)}
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  className={`p-1.5 rounded-lg transition-colors group ${isDark ? 'hover:bg-white/10' : 'hover:bg-slate-200 sm:hover:bg-white/10'}`}
                                  title="Edit"
                                  disabled={isLoading}
                                >
                                  <Edit3 className={`w-3.5 h-3.5 ${isDark ? 'text-white/70 group-hover:text-white' : 'text-slate-500 group-hover:text-slate-700 sm:text-white/70 sm:group-hover:text-white'}`} />
                                </motion.button>
                                <motion.button
                                  onClick={() => handleReprompt(message)}
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  className={`p-1.5 rounded-lg transition-colors group ${isDark ? 'hover:bg-white/10' : 'hover:bg-slate-200 sm:hover:bg-white/10'}`}
                                  title="Use as prompt"
                                  disabled={isLoading}
                                >
                                  <CornerUpLeft className={`w-3.5 h-3.5 ${isDark ? 'text-white/70 group-hover:text-white' : 'text-slate-500 group-hover:text-slate-700 sm:text-white/70 sm:group-hover:text-white'}`} />
                                </motion.button>
                              </div>
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          <MarkdownRenderer content={message.content} className="text-[13px] sm:text-sm break-words overflow-hidden" isDark={isDark} />
                          {/* Show generated images */}
                          {message.images && message.images.length > 0 && (
                            <div className="mt-3 sm:mt-4 space-y-3 sm:space-y-4">
                              {message.images.map((img, imgIndex) => (
                                <div key={imgIndex} className={`relative group ${isDark ? 'bg-dark-700/50' : 'bg-slate-100'} rounded-xl sm:rounded-2xl p-2 sm:p-3`}>
                                  {/* Image label */}
                                  <div className={`flex items-center justify-between mb-2 px-1 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                                    <span className="text-xs sm:text-sm font-medium">
                                      {img.style ? `${img.style.charAt(0).toUpperCase() + img.style.slice(1)} Design` : `Design ${imgIndex + 1}`}
                                    </span>
                                    <span className="text-xs">Variation {imgIndex + 1} of {message.images?.length}</span>
                                  </div>
                                  {/* Image */}
                                  <img
                                    src={img.url}
                                    alt={img.style || `Generated design ${imgIndex + 1}`}
                                    className={`w-full rounded-lg sm:rounded-xl cursor-pointer hover:opacity-90 transition-all border ${isDark ? 'border-white/10 hover:border-purple-500/50' : 'border-slate-200 hover:border-purple-400'}`}
                                    onClick={() => setShowImagePreview(img.url)}
                                  />
                                  {/* Download button - always visible on mobile, hover on desktop */}
                                  <div className="flex items-center justify-between mt-2 sm:mt-3">
                                    <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                                      Click to enlarge
                                    </span>
                                    <motion.button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDownloadImage(img.url, `design-variation-${imgIndex + 1}.png`);
                                      }}
                                      whileHover={{ scale: 1.05 }}
                                      whileTap={{ scale: 0.95 }}
                                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                                        isDark
                                          ? 'bg-purple-500/20 text-purple-300 hover:bg-purple-500/30'
                                          : 'bg-purple-100 text-purple-600 hover:bg-purple-200'
                                      }`}
                                    >
                                      <Download className="w-3.5 h-3.5" />
                                      <span>Download</span>
                                    </motion.button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Route Suggestions - Connect with Specialist buttons */}
                          {message.routeSuggestions && message.routeSuggestions.length > 0 && (
                            <div className="mt-4 space-y-2">
                              <div className={`flex items-center gap-2 text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                                <Users className="w-3.5 h-3.5" />
                                <span>Recommended Specialists:</span>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {message.routeSuggestions.map((suggestion, idx) => {
                                  const targetEmployee = getEmployeeById(suggestion.employeeId);
                                  // Find the user's message that triggered this routing suggestion
                                  const userMessage = messages.slice(0, index).reverse().find(m => m.role === 'user');
                                  const routedMessage = userMessage?.content || '';
                                  return (
                                    <motion.button
                                      key={idx}
                                      onClick={() => {
                                        // Navigate with the original user message as a routed parameter
                                        // Use encodeURIComponent for proper URL encoding
                                        const params = new URLSearchParams();
                                        params.set('routed', 'true');
                                        params.set('from', employeeId || 'agent');
                                        params.set('message', encodeURIComponent(routedMessage));
                                        navigate(`/dashboard/employees/${suggestion.employeeId}?${params.toString()}`);
                                      }}
                                      whileHover={{ scale: 1.02 }}
                                      whileTap={{ scale: 0.98 }}
                                      className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all ${
                                        isDark
                                          ? 'bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border-purple-500/30 hover:border-purple-500/50 text-white'
                                          : 'bg-gradient-to-r from-purple-50 to-cyan-50 border-purple-200 hover:border-purple-400 text-slate-900'
                                      }`}
                                    >
                                      {targetEmployee && (
                                        <div className={`w-6 h-6 rounded-lg bg-gradient-to-br ${targetEmployee.gradient} flex items-center justify-center`}>
                                          <span className="text-xs">{targetEmployee.avatar}</span>
                                        </div>
                                      )}
                                      <div className="text-left">
                                        <div className="text-sm font-medium">Connect with {suggestion.employeeName}</div>
                                        <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                                          {suggestion.employeeRole}
                                        </div>
                                      </div>
                                      <ArrowRight className={`w-4 h-4 ml-1 ${isDark ? 'text-purple-400' : 'text-purple-500'}`} />
                                    </motion.button>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Cross-Employee Collaboration Results */}
                          {message.collaborations && message.collaborations.length > 0 && (
                            <div className="mt-4 space-y-3">
                              <div className={`flex items-center gap-2 text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                                <Users className="w-3.5 h-3.5" />
                                <span>Collaboration Results:</span>
                              </div>
                              {message.collaborations.map((collab, idx) => {
                                const collabEmployee = getEmployeeById(collab.employeeId);
                                return (
                                  <div
                                    key={idx}
                                    className={`rounded-xl border p-4 ${
                                      isDark
                                        ? 'bg-gradient-to-r from-cyan-500/5 to-purple-500/5 border-cyan-500/20'
                                        : 'bg-gradient-to-r from-cyan-50 to-purple-50 border-cyan-200'
                                    }`}
                                  >
                                    <div className="flex items-center gap-2 mb-3">
                                      {collabEmployee && (
                                        <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${collabEmployee.gradient} flex items-center justify-center`}>
                                          <span className="text-sm">{collabEmployee.avatar}</span>
                                        </div>
                                      )}
                                      <div>
                                        <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                          {collab.employeeName}
                                        </div>
                                        <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                                          {collab.employeeRole}
                                        </div>
                                      </div>
                                    </div>
                                    <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                                      <MarkdownRenderer content={collab.content.replace(/\[EDIT_IMAGE\][\s\S]*?\[\/EDIT_IMAGE\]/g, '').trim()} />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Generated Presentations */}
                          {message.presentations && message.presentations.length > 0 && (
                            <div className="mt-4 space-y-4">
                              {message.presentations.map((presentation, presIdx) => (
                                <div
                                  key={presIdx}
                                  className={`rounded-2xl border overflow-hidden ${
                                    isDark
                                      ? 'bg-gradient-to-br from-orange-500/10 via-red-500/5 to-purple-500/10 border-orange-500/30'
                                      : 'bg-gradient-to-br from-orange-50 via-red-50 to-purple-50 border-orange-200'
                                  }`}
                                >
                                  {/* Presentation Header */}
                                  <div className={`p-4 border-b ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                                    <div className="flex items-start justify-between">
                                      <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-lg`}>
                                          <span className="text-lg">📊</span>
                                        </div>
                                        <div>
                                          <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                            {presentation.data.title}
                                          </h3>
                                          {presentation.data.subtitle && (
                                            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                                              {presentation.data.subtitle}
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
                                        isDark ? 'bg-orange-500/20 text-orange-300' : 'bg-orange-100 text-orange-600'
                                      }`}>
                                        <Zap className="w-3 h-3" />
                                        <span>{presentation.credits} credits</span>
                                      </div>
                                    </div>
                                    <div className={`flex items-center gap-4 mt-3 text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                                      <span className="flex items-center gap-1">
                                        <span className="font-medium">{presentation.data.slides.length}</span> slides
                                      </span>
                                      <span className="capitalize">{presentation.data.style} style</span>
                                      <span className="capitalize">{presentation.data.theme} theme</span>
                                    </div>
                                  </div>

                                  {/* Slides Preview */}
                                  <div className="p-4 max-h-[500px] overflow-y-auto">
                                    <div className="space-y-3">
                                      {presentation.data.slides.map((slide, slideIdx) => (
                                        <motion.div
                                          key={slideIdx}
                                          initial={{ opacity: 0, y: 10 }}
                                          animate={{ opacity: 1, y: 0 }}
                                          transition={{ delay: slideIdx * 0.05 }}
                                          className={`rounded-xl border p-4 ${
                                            isDark
                                              ? 'bg-dark-800/50 border-white/10 hover:border-orange-500/30'
                                              : 'bg-white border-slate-200 hover:border-orange-300'
                                          } transition-colors`}
                                        >
                                          <div className="flex items-start gap-3">
                                            <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${
                                              isDark
                                                ? 'bg-gradient-to-br from-orange-500/20 to-red-500/20 text-orange-400'
                                                : 'bg-gradient-to-br from-orange-100 to-red-100 text-orange-600'
                                            }`}>
                                              {slide.number}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                              <h4 className={`font-semibold text-sm mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                                {slide.title}
                                              </h4>
                                              <ul className={`space-y-1.5 text-sm ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                                                {slide.content.map((point, pointIdx) => (
                                                  <li key={pointIdx} className="flex items-start gap-2">
                                                    <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                                      isDark ? 'bg-orange-400' : 'bg-orange-500'
                                                    }`} />
                                                    <span>{point}</span>
                                                  </li>
                                                ))}
                                              </ul>
                                              {slide.notes && (
                                                <div className={`mt-3 pt-2 border-t text-xs italic ${
                                                  isDark ? 'border-white/5 text-gray-500' : 'border-slate-100 text-slate-400'
                                                }`}>
                                                  💡 {slide.notes}
                                                </div>
                                              )}
                                              {(slide.animation || slide.transition) && (
                                                <div className={`flex items-center gap-3 mt-2 text-xs ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                                                  {slide.animation && (
                                                    <span className="flex items-center gap-1">
                                                      ✨ {slide.animation}
                                                    </span>
                                                  )}
                                                  {slide.transition && (
                                                    <span className="flex items-center gap-1">
                                                      🔄 {slide.transition}
                                                    </span>
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </motion.div>
                                      ))}
                                    </div>
                                  </div>

                                  {/* Export Actions */}
                                  <div className={`p-4 border-t ${isDark ? 'border-white/10 bg-dark-800/30' : 'border-slate-200 bg-slate-50'}`}>
                                    <div className="flex flex-wrap items-center gap-2">
                                      <motion.button
                                        onClick={() => {
                                          const json = JSON.stringify(presentation.data, null, 2);
                                          const blob = new Blob([json], { type: 'application/json' });
                                          const url = URL.createObjectURL(blob);
                                          const a = document.createElement('a');
                                          a.href = url;
                                          a.download = `${presentation.data.title.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.json`;
                                          a.click();
                                          URL.revokeObjectURL(url);
                                        }}
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                          isDark
                                            ? 'bg-orange-500/20 text-orange-300 hover:bg-orange-500/30'
                                            : 'bg-orange-100 text-orange-600 hover:bg-orange-200'
                                        }`}
                                      >
                                        <Download className="w-4 h-4" />
                                        <span>Export JSON</span>
                                      </motion.button>
                                      <motion.button
                                        onClick={() => {
                                          navigator.clipboard.writeText(JSON.stringify(presentation.data, null, 2));
                                          setCopiedId(`pres-${presentation.id}`);
                                          setTimeout(() => setCopiedId(null), 2000);
                                        }}
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                          isDark
                                            ? 'bg-white/5 text-gray-300 hover:bg-white/10'
                                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                        }`}
                                      >
                                        {copiedId === `pres-${presentation.id}` ? (
                                          <>
                                            <Check className="w-4 h-4 text-green-400" />
                                            <span>Copied!</span>
                                          </>
                                        ) : (
                                          <>
                                            <Copy className="w-4 h-4" />
                                            <span>Copy JSON</span>
                                          </>
                                        )}
                                      </motion.button>
                                    </div>
                                    <p className={`mt-2 text-xs ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                                      Export to use in Gamma, Canva, PowerPoint, or Google Slides
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Connector Results (READ actions) */}
                          {message.connectorResults && message.connectorResults.length > 0 && (
                            <div className="mt-4 space-y-3">
                              <div className={`flex items-center gap-2 text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                                <Zap className="w-3.5 h-3.5" />
                                <span>Integration Data:</span>
                              </div>
                              {message.connectorResults.map((result, idx) => (
                                <ConnectorResultCard
                                  key={idx}
                                  provider={result.provider}
                                  providerName={result.providerName}
                                  action={result.action}
                                  success={result.success}
                                  data={result.data}
                                  error={result.error}
                                />
                              ))}
                            </div>
                          )}

                          {/* Connector Pending Actions (WRITE actions needing approval) */}
                          {message.pendingActions && message.pendingActions.length > 0 && (
                            <div className="mt-4 space-y-3">
                              <div className={`flex items-center gap-2 text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                                <Zap className="w-3.5 h-3.5" />
                                <span>Actions:</span>
                              </div>
                              {message.pendingActions.map((action, idx) => (
                                <ConnectorActionCard
                                  key={idx}
                                  action={action}
                                  onApprove={handleApproveAction}
                                  onEdit={handleEditAction}
                                  onReject={handleRejectAction}
                                  isLoading={actionLoadingId === action.id}
                                  status={action.status}
                                  result={action.result}
                                />
                              ))}
                            </div>
                          )}
                        </>
                      )}

                      {/* Message Actions (for assistant messages) - Now visible on mobile too */}
                      {message.role === 'assistant' && (
                        <div className={`flex items-center gap-1 mt-2 sm:mt-3 pt-2 border-t ${isDark ? 'border-white/5' : 'border-slate-100'}`}>
                          <motion.button
                            onClick={() => handleCopy(message.content, message.id)}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            className={`p-1.5 rounded-lg transition-colors group ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-100'}`}
                            title="Copy"
                          >
                            {copiedId === message.id ? (
                              <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-400" />
                            ) : (
                              <Copy className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${isDark ? 'text-gray-500 group-hover:text-gray-300' : 'text-slate-400 group-hover:text-slate-600'}`} />
                            )}
                          </motion.button>
                          <motion.button
                            onClick={() => handleSpeak(message.content, message.id)}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            className={`p-1.5 rounded-lg transition-colors group ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-100'}`}
                            title={speakingMessageId === message.id ? "Stop" : "Listen"}
                          >
                            {speakingMessageId === message.id ? (
                              <VolumeX className={`w-3.5 h-3.5 sm:w-4 sm:h-4 text-purple-400`} />
                            ) : (
                              <Volume2 className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${isDark ? 'text-gray-500 group-hover:text-gray-300' : 'text-slate-400 group-hover:text-slate-600'}`} />
                            )}
                          </motion.button>
                          <motion.button
                            onClick={handleRegenerate}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            className={`p-1.5 rounded-lg transition-colors group hidden sm:block ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-100'}`}
                            title="Regenerate"
                          >
                            <RefreshCw className={`w-4 h-4 ${isDark ? 'text-gray-500 group-hover:text-gray-300' : 'text-slate-400 group-hover:text-slate-600'}`} />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            className={`p-1.5 rounded-lg transition-colors group ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-100'}`}
                            title="Like"
                          >
                            <ThumbsUp className={`w-3.5 h-3.5 sm:w-4 sm:h-4 group-hover:text-green-400 ${isDark ? 'text-gray-500' : 'text-slate-400'}`} />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            className={`p-1.5 rounded-lg transition-colors group ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-100'}`}
                            title="Dislike"
                          >
                            <ThumbsDown className={`w-3.5 h-3.5 sm:w-4 sm:h-4 group-hover:text-red-400 ${isDark ? 'text-gray-500' : 'text-slate-400'}`} />
                          </motion.button>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {/* Streaming Response */}
            {streamingContent && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex justify-start"
                layout={false}
              >
                <div className="flex gap-3 max-w-[85%] lg:max-w-[75%]">
                  <div className="flex-shrink-0 mt-1">
                    <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${employee.gradient} flex items-center justify-center shadow-lg`}>
                      <span className="text-sm">{employee.avatar}</span>
                    </div>
                  </div>
                  <div
                    className={`rounded-2xl rounded-tl-sm px-4 py-3 backdrop-blur-sm border ${isDark ? 'bg-dark-800/80 border-white/5' : 'bg-white/90 border-slate-200 shadow-sm'}`}
                    style={{ contain: 'layout style', minWidth: '200px' }}
                  >
                    <div className={`flex items-center gap-2 mb-2 pb-2 border-b ${isDark ? 'border-white/5' : 'border-slate-100'}`}>
                      <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{employee.name}</span>
                      <span className="text-xs text-purple-400">typing...</span>
                    </div>
                    <div className="text-sm leading-relaxed" style={{ contain: 'layout' }}>
                      <MarkdownRenderer content={streamingContent} isDark={isDark} />
                      <span
                        className="inline-block w-2 h-4 bg-purple-400 ml-0.5 animate-pulse"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Loading Indicator */}
            {isLoading && !streamingContent && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex justify-start"
              >
                <div className="flex gap-3">
                  <div className="flex-shrink-0 mt-1">
                    <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${employee.gradient} flex items-center justify-center shadow-lg`}>
                      <span className="text-sm">{employee.avatar}</span>
                    </div>
                  </div>
                  <div className={`px-4 py-3 backdrop-blur-sm border rounded-2xl rounded-tl-sm ${isDark ? 'bg-dark-800/80 border-white/5' : 'bg-white/90 border-slate-200 shadow-sm'}`}>
                    <div className="flex items-center gap-3">
                      <div className="flex gap-1">
                        {[0, 1, 2].map((i) => (
                          <motion.div
                            key={i}
                            className="w-2 h-2 bg-purple-400 rounded-full"
                            animate={{ y: [0, -6, 0] }}
                            transition={{
                              duration: 0.6,
                              repeat: Infinity,
                              delay: i * 0.15,
                            }}
                          />
                        ))}
                      </div>
                      <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>{employee.name} is thinking...</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area - ChatGPT style - sticky on mobile to stay above keyboard */}
        <div className={`flex-shrink-0 px-4 py-3 sm:p-4 lg:px-8 border-t safe-area-bottom sticky bottom-0 ${isDark ? 'border-white/5 bg-dark-900 sm:bg-dark-800/50' : 'border-slate-200 bg-white sm:bg-white/80'}`}>
          <div className="max-w-3xl mx-auto">
            {/* Uploaded image preview */}
            {uploadedImage && (
              <div className="mb-2 flex items-start gap-2">
                <div className="relative">
                  <img
                    src={uploadedImage}
                    alt="Uploaded"
                    className={`h-16 sm:h-20 rounded-lg border ${isDark ? 'border-white/10' : 'border-slate-200'}`}
                  />
                  <button
                    onClick={() => setUploadedImage(null)}
                    className="absolute -top-1.5 -right-1.5 p-1 bg-red-500 rounded-full text-white hover:bg-red-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )}

            {/* Image generating indicator */}
            {isGeneratingImage && (
              <div className="mb-2 flex items-center gap-2 px-3 py-2 bg-purple-500/10 rounded-xl border border-purple-500/20">
                <Loader2 className="w-4 h-4 text-purple-400 animate-spin flex-shrink-0" />
                <span className="text-xs sm:text-sm text-purple-300">Generating designs...</span>
              </div>
            )}

            {/* ChatGPT-style input container */}
            <div className={`relative flex items-end rounded-2xl border transition-colors ${isDark ? 'bg-dark-700 border-white/10 focus-within:border-purple-500/50' : 'bg-slate-100 border-slate-200 focus-within:border-purple-400'}`}>
              {/* Attachment Button */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept="image/*"
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className={`flex-shrink-0 p-3 rounded-xl transition-colors ${
                  employeeId === 'pixel'
                    ? 'hover:bg-pink-500/10 text-pink-400'
                    : 'hover:bg-white/5 text-gray-400'
                }`}
                title={employeeId === 'pixel' ? 'Upload image to edit' : 'Attach file'}
              >
                <Paperclip className="w-5 h-5" />
              </button>

              {/* Text Input - 16px font prevents iOS zoom on focus */}
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={employeeId === 'pixel'
                  ? 'Describe your design...'
                  : `Message ${employee.name}...`
                }
                rows={1}
                disabled={isLoading || isGeneratingImage}
                className={`flex-1 py-3 pr-2 bg-transparent text-base resize-none focus:outline-none max-h-[120px] min-h-[24px] disabled:opacity-50 ${isDark ? 'text-white placeholder-gray-500' : 'text-slate-900 placeholder-slate-400'}`}
                style={{ lineHeight: '1.5', fontSize: '16px' }}
              />

              {/* Voice Button */}
              <button
                className={`flex-shrink-0 p-3 rounded-xl transition-colors hidden sm:block ${isDark ? 'hover:bg-white/5 text-gray-400' : 'hover:bg-slate-200 text-slate-400'}`}
              >
                <Mic className="w-5 h-5" />
              </button>

              {/* Send Button */}
              <button
                onClick={() => handleSend()}
                disabled={!input.trim() || isLoading || isGeneratingImage}
                className={`flex-shrink-0 p-3 m-1 rounded-xl transition-all ${
                  input.trim() && !isLoading && !isGeneratingImage
                    ? 'bg-purple-500 hover:bg-purple-600 text-white'
                    : isDark ? 'bg-dark-600 text-gray-500 cursor-not-allowed' : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                }`}
              >
                {isLoading || isGeneratingImage ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>

            {/* Credits Info - Compact on mobile */}
            <div className="flex items-center justify-center mt-2 gap-1.5">
              <Sparkles className="w-3 h-3 text-purple-400" />
              <span className={`text-[10px] sm:text-xs ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                {user?.credits_remaining?.toLocaleString() || 0} credits
              </span>
              {employeeId === 'pixel' && (
                <span className={`text-[10px] hidden sm:inline ml-2 ${isDark ? 'text-gray-600' : 'text-slate-400'}`}>
                  • Tip: Ask me to create logos or graphics!
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Image Preview Modal */}
      <AnimatePresence>
        {showImagePreview && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
            onClick={() => setShowImagePreview(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative max-w-4xl max-h-[90vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={showImagePreview}
                alt="Preview"
                className="max-w-full max-h-[90vh] rounded-xl object-contain"
              />
              <div className="absolute top-4 right-4 flex gap-2">
                <motion.button
                  onClick={() => handleDownloadImage(showImagePreview, 'design.png')}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="p-3 bg-purple-500 rounded-xl text-white hover:bg-purple-600"
                  title="Download"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                </motion.button>
                <motion.button
                  onClick={() => setShowImagePreview(null)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="p-3 bg-white/10 rounded-xl text-white hover:bg-white/20"
                >
                  <X className="w-5 h-5" />
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Connector Edit Modal */}
      <ConnectorEditModal
        isOpen={isEditModalOpen}
        action={editingAction}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingAction(null);
        }}
        onSave={handleSaveEditedAction}
        isLoading={actionLoadingId === editingAction?.id}
      />
    </div>
  );
}
